import { AfterViewInit, Directive, ElementRef, HostListener, Input, Renderer2, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarComponent, SnackbarType } from 'src/app/core/snackbar/snackbar.component';
import { precisionRound, roundTo } from 'src/app/utils/Utils';

@Directive({
  selector: 'input[appUnitConversion]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UnitConversionDirective),
      multi: true,
    },
  ],
  exportAs: 'appUnitConversion',
})
export class UnitConversionDirective implements ControlValueAccessor, AfterViewInit {
  private _onChange?: (value: number) => void;
  private _onTouched?: () => void;
  private _value = 0;

  @Input() appUnitConversion = 100;
  @Input() unit = '';

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private snackbar: MatSnackBar,
    private translateService: TranslateService,
  ) {}

  ngAfterViewInit() {
    this.ensureWrapper();
    this.addUnitLabel();
    const element = this.el.nativeElement;

    if (element instanceof HTMLInputElement) {
      // Assuming initial min, max, and step are bound or otherwise set
      this.renderer.setProperty(element, 'min', (Number(element.min) * this.appUnitConversion).toString());
      this.renderer.setProperty(element, 'max', (Number(element.max) * this.appUnitConversion).toString());
      this.renderer.setProperty(element, 'step', (Number(element.step) * this.appUnitConversion).toString());

      // Adjust the input based on the new calculations
      if (element.value) {
        element.value = (Number(element.value) * this.appUnitConversion).toString();
        element.dispatchEvent(new Event('input'));
      }
    }
  }

  private ensureWrapper() {
    const parent = this.el.nativeElement.parentNode;
    if (parent.tagName !== 'DIV' || !parent.classList.contains('unit-conversion-wrapper')) {
      // Create wrapper div
      const wrapper = this.renderer.createElement('div');
      this.renderer.addClass(wrapper, 'unit-conversion-wrapper');
      this.renderer.setStyle(wrapper, 'position', 'relative');
      this.renderer.setStyle(wrapper, 'display', 'inline-block');
      this.renderer.setStyle(wrapper, 'width', '100%');

      // Move the input inside the wrapper
      this.renderer.insertBefore(parent, wrapper, this.el.nativeElement);

      this.renderer.appendChild(wrapper, this.el.nativeElement);
      //this.renderer.appendChild(parent, wrapper);

      // Apply styles to input to fit within the new wrapper context
      this.renderer.setStyle(this.el.nativeElement, 'width', '100%');
    }
  }

  private _handleInputError(element: HTMLInputElement) {
    const parent = element.parentNode as HTMLElement;
    if (!parent) return;

    /*  // Remove existing error message if present
    const existingErrors = parent.querySelectorAll('.unit-error-message');
    existingErrors.forEach((errEl) => {
      this.renderer.removeChild(parent, errEl);
    }); */

    const errorMessage = this.translateService.instant('translations.invalid-numeric-input', {
      min: roundTo(Number(element.min), 2),
      max: roundTo(Number(element.max), 2),
    });

    // Create error message element
    const errorEl = this.renderer.createElement('div');
    this.renderer.addClass(errorEl, 'unit-error-message');
    this.renderer.setStyle(errorEl, 'color', 'red');
    this.renderer.setStyle(errorEl, 'font-size', '12px');
    this.renderer.setStyle(errorEl, 'margin-top', '10px');
    this.renderer.setStyle(errorEl, 'bottom', '-18px'); // adjust based on layout
    this.renderer.setStyle(errorEl, 'left', '0');
    this.renderer.setStyle(errorEl, 'right', '0');

    const errorText = this.renderer.createText(errorMessage);
    this.renderer.appendChild(errorEl, errorText);
    this.renderer.appendChild(parent, errorEl);
  }

  private addUnitLabel() {
    // Check if label already exists to prevent duplicates
    if (!(this.el.nativeElement instanceof HTMLInputElement)) return;
    const parent = this.el.nativeElement.parentNode;
    if (!parent) return;
    const siblings = parent.childNodes;
    const hasLabel = Array.from(siblings).some((node: Node) => {
      // We check if the node is an HTMLElement to access the tagName property
      return node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'P';
    });
    if (!hasLabel) {
      // Create the unit label paragraph
      const label = this.renderer.createElement('p');
      this.renderer.addClass(label, 'unit-label');
      this.renderer.setStyle(label, 'position', 'absolute');
      this.renderer.setStyle(label, 'top', '0px');
      this.renderer.setStyle(label, 'right', '5px');
      this.renderer.setStyle(label, 'opacity', '0.6');
      this.renderer.setStyle(label, 'font-size', '12px');
      this.renderer.setStyle(label, 'margin', '0px');
      this.renderer.setStyle(label, 'padding', '10px');
      const text = this.renderer.createText(this.unit);
      this.renderer.appendChild(label, text);
      // Append label to the parent (wrapper)
      this.renderer.appendChild(this.el.nativeElement.parentNode, label);
    }
  }

  writeValue(value: number): void {
    this._value = value;
    const displayValue = value != null ? precisionRound(value * this.appUnitConversion, 2).toString() : '';
    this.renderer.setProperty(this.el.nativeElement, 'value', displayValue);
  }

  registerOnChange(fn: (value: number) => void): void {
    //To obvesti model, da je prišlo do spremembe
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    //to obvesti model, da je user interactal z kontroli
    this._onTouched = fn;
  }

  public checkValidity() {
    const element = this.el.nativeElement as HTMLInputElement;

    const parent = element.parentNode as HTMLElement;
    const existingErrors = parent.querySelectorAll('.unit-error-message');

    existingErrors.forEach((errEl) => {
      this.renderer.removeChild(parent, errEl);
    });

    let numVal = Number(element.value) / this.appUnitConversion;

    const max = Number(element.max) / this.appUnitConversion;
    const min = Number(element.min) / this.appUnitConversion;
    const step = Number(element.step) / this.appUnitConversion;

    if (numVal > max) {
      numVal = max;
      this._handleInputError(element);
    } else if (numVal < min) {
      numVal = min;
      this._handleInputError(element);
    }

    const transformedValue = this._roundToStep(numVal, step);
    const displayValue = precisionRound(transformedValue * this.appUnitConversion, 2);

    // Update the internal value
    this._value = transformedValue;

    // Update the input element
    //this.renderer.setProperty(element, 'value', displayValue.toString());

    // Notify of the change
    if (this._onChange) {
      this._onChange(this._value);
    }

    // Dispatch the input event
    /* element.dispatchEvent(new Event('input')); */
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Allow numbers, a single dot, and ensure dot is not the first character
    if (/^(?!\.)\d*\.?\d*$/.test(value)) {
      if (value === '.') {
        // If only a dot is entered, prepend a zero
        input.value = '0.';
        this._value = 0;
      } else {
        const numValue = value === '' ? 0 : parseFloat(value) / this.appUnitConversion;

        if (!isNaN(numValue)) {
          this._value = numValue; // Store the exact value without rounding

          if (this._onChange) {
            this._onChange(this._value);
          }
        }
      }
    } else {
      // If invalid input, revert to the last valid value
      const displayValue = precisionRound(this._value * this.appUnitConversion, 2);
      this.renderer.setProperty(input, 'value', displayValue.toString());
    }

    if (this._onTouched) {
      this._onTouched();
    }
  }

  @HostListener('blur')
  onBlur(): void {
    // On blur, format the input to show the full converted value
    const formattedValue = precisionRound(this._value * this.appUnitConversion, 2).toString();
    this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue);

    if (this._onTouched) {
      this._onTouched();
    }
  }

  private _roundToStep(value: number, step: number): number {
    if (step === 0) return value;
    const inverse = 1 / step;
    return Math.round(value * inverse) / inverse;
  }
}
